import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { Field, Btn } from "@/components/shared";

const TONE = {
  red: "bg-red-50 text-red-700 border-red-200", amber: "bg-amber-50 text-amber-800 border-amber-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200", slate: "bg-slate-100 text-slate-600 border-slate-200", blue: "bg-blue-50 text-blue-700 border-blue-200",
};
const CHIP = {
  OPEN: "red", MISMATCH: "red", OVERDUE: "red", FAILED: "red", REJECTED: "red",
  PENDING: "amber", PARTIAL: "amber", PENDING_APPROVAL: "amber", CLOSED_UNEXPLAINED: "amber", PROCESSING: "amber", NOT_PROVIDED: "amber", PRESENT_UNVERIFIED: "amber",
  MATCHED: "green", RESOLVED: "green", VERIFIED: "green", POSTED: "green", RECOVERED: "green", APPROVED: "green", SUCCESS: "green", DONE: "green",
  REFUNDED: "blue", NA: "slate", RUNNING: "blue",
};
const LABEL = { CLOSED_UNEXPLAINED: "CLOSED — UNEXPLAINED", NOT_PROVIDED: "NOT PROVIDED BY VENDOR", PRESENT_UNVERIFIED: "SIGNATURE NOT VERIFIABLE", NA: "N/A", PENDING_APPROVAL: "PENDING APPROVAL" };

export const money = (v) => (v === null || v === undefined ? "—" : inr(v));

export function Chip({ s, testid }) {
  return <span className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-bold tracking-wide ${TONE[CHIP[s] || "slate"]}`} data-testid={testid || "recon-chip"}>{LABEL[s] || s || "—"}</span>;
}

export function Age({ h }) {
  if (h === null || h === undefined) return <span>—</span>;
  const tone = h < 24 ? "slate" : h < 72 ? "amber" : "red";
  return <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${TONE[tone]}`} data-testid="age-badge">{h < 24 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`}</span>;
}

export const call = async (fn, msg) => {
  try { const r = await fn(); if (msg) toast.success(msg); return r?.data ?? true; } catch (e) { toast.error(errMsg(e)); return null; }
};

export async function exportRows(name, rows, cols) {
  const use = cols.filter((c) => c.key || c.csv);
  try {
    const body = { name, headers: use.map((c) => c.label), rows: rows.map((r) => use.map((c) => String(c.csv ? c.csv(r) : r[c.key] ?? ""))) };
    const { data } = await api.post("/admin/recon/export", body, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `${name}.xlsx`;
    a.click();
  } catch (e) { toast.error(errMsg(e)); }
}

export function ExportBtn({ name, rows, cols }) {
  return <Btn variant="outline" onClick={() => exportRows(name, rows, cols)} data-testid={`export-${name}`}><Download className="h-4 w-4" />Export Excel</Btn>;
}

export function EvidenceLink({ ev }) {
  if (!ev) return null;
  if (!ev.startsWith("file:")) return <span className="text-xs text-slate-600">{ev}</span>;
  const open = async () => {
    try {
      const { data } = await api.get("/admin/recon/evidence-file", { params: { path: ev.slice(5) }, responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <button type="button" onClick={open} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700" data-testid="evidence-file-link"><Paperclip className="h-3 w-3" />file</button>;
}

export function EvidenceInput({ value, onChange, testid = "evidence" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const r = await call(() => api.post("/admin/recon/evidence", fd), "Evidence uploaded");
    if (r) onChange(r.evidence);
    setBusy(false);
  };
  return (
    <Field label="Evidence (reference / link, or upload a file)">
      <div className="flex gap-2">
        <input className="np-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. bank statement page 3, vendor ticket #123" data-testid={`${testid}-input`} />
        <label className="np-input flex w-auto cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-semibold" data-testid={`${testid}-upload`}>
          <Paperclip className="h-3.5 w-3.5" />{busy ? "Uploading…" : "File"}<input type="file" className="hidden" onChange={upload} />
        </label>
      </div>
    </Field>
  );
}

export function Modal({ open, onClose, title, testid, children, wide }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`} data-testid={testid} aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function ReasonDialog({ open, title, onClose, onSubmit, children, testid = "reason-dialog", cta = "Submit" }) {
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  useEffect(() => { if (open) { setReason(""); setEvidence(""); } }, [open]);
  return (
    <Modal open={open} onClose={onClose} title={title} testid={testid}>
      <div className="space-y-3">
        {children}
        <Field label="Reason"><textarea className="np-input min-h-[70px]" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} /></Field>
        <EvidenceInput value={evidence} onChange={setEvidence} testid={`${testid}-evidence`} />
        <Btn onClick={() => onSubmit({ reason, evidence })} disabled={reason.trim().length < 5 || evidence.trim().length < 3} data-testid={`${testid}-submit`}>{cta}</Btn>
        <div className="text-[11px] text-slate-500">Reason (min 5 characters) and evidence are required. This action is recorded in the audit log.</div>
      </div>
    </Modal>
  );
}

export function MemberPicker({ value, onChange, testid = "member-picker" }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => api.get("/admin/recon/members", { params: { q } }).then(({ data }) => setList(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Field label="Member (incl. downline)">
      <div className="relative">
        <input className="np-input w-52" value={value ? value.label : q} placeholder="Login ID / name" onFocus={() => setOpen(true)}
          onChange={(e) => { onChange(null); setQ(e.target.value); }} data-testid={testid} />
        {open && !value && list.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-56 w-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
            {list.map((u) => (
              <button type="button" key={u.id} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50" data-testid={`${testid}-opt-${u.login_id}`}
                onClick={() => { onChange({ id: u.id, label: `${u.login_id} · ${u.name}` }); setOpen(false); }}>
                <b className="font-mono">{u.login_id}</b> · {u.name} <span className="text-slate-400">{u.role}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

export function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono text-sm font-bold ${tone === "bad" ? "text-red-700" : tone === "good" ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export const when = (s) => fmtDate(s);
